import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useToast } from '../../contexts/ToastContext';
import { CopyableAddress } from '../common/CopyButton';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import '../../styles/bot-config-modal.css';

const BotConfigModal = ({
  isOpen,
  onClose,
  token,
  existingPosition = null
}) => {
  const { tokens, isAuthenticated } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError } = useToast();

  // Modal state
  const [currentStep, setCurrentStep] = useState('strategy'); // strategy, parameters, advanced, confirmation, creating
  const [loading, setLoading] = useState(false);

  // Bot configuration state
  const [strategy, setStrategy] = useState('conservative');
  const [parameters, setParameters] = useState({
    stopLossPercentage: 10,
    takeProfitPercentage: 25,
    trailingStopEnabled: true,
    positionSize: 0.1,
    reentryEnabled: true,
    initialPurchase: true,
    walletId: '',
    // Advanced trading features
    advancedFeaturesEnabled: false,
    multipleTakeProfits: [
      { percentage: 15, positionPercentage: 30 },
      { percentage: 30, positionPercentage: 40 },
      { percentage: 50, positionPercentage: 30 }
    ],
    dynamicStopLossEnabled: false,
    atrMultiplier: 2.0,
    partialTradingEnabled: false,
    timeBasedExitEnabled: false,
    timeExitDurationHours: 24,
    volumeSpikeDetection: false,
    volumeSpikeThreshold: 200,
    priceTargetsEnabled: false,
    priceTargets: []
  });

  // Real-time data
  const [walletBalances, setWalletBalances] = useState({});
  const [tokenInfo, setTokenInfo] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [tradingFees, setTradingFees] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  // Strategy templates with predefined parameters
  const strategies = {
    conservative: {
      name: 'Conservative',
      description: 'Low risk strategy with tight stop-losses and moderate profit targets',
      icon: '🛡️',
      stopLoss: 5,
      takeProfit: 15,
      trailing: true,
      reentry: true,
      color: '#28a745'
    },
    aggressive: {
      name: 'Aggressive',
      description: 'High risk, high reward strategy with wider stop-losses',
      icon: '🚀',
      stopLoss: 15,
      takeProfit: 50,
      trailing: true,
      reentry: true,
      color: '#dc3545'
    },
    scalping: {
      name: 'Scalping',
      description: 'Quick trades with small profits and tight stop-losses',
      icon: '⚡',
      stopLoss: 3,
      takeProfit: 8,
      trailing: false,
      reentry: true,
      color: '#ffc107'
    },
    hodl: {
      name: 'HODL',
      description: 'Long-term holding with wide stop-losses for major downturns',
      icon: '💎',
      stopLoss: 25,
      takeProfit: 100,
      trailing: true,
      reentry: false,
      color: '#6f42c1'
    }
  };

  // Get platform wallets for trading
  const platformWallets = wallets.filter(w => w.wallet_type === 'platform');
  const selectedWallet = platformWallets.find(w => w.id.toString() === parameters.walletId);

  // Authenticated request helper
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('Please log in to configure bot');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  }, [tokens?.access_token, API_BASE_URL]);

  // Initialize modal when opened
  useEffect(() => {
    if (isOpen && token) {
      setCurrentStep('strategy');
      setStrategy('conservative');

      // Set initial parameters based on existing position or defaults
      const initialParams = {
        stopLossPercentage: strategies.conservative.stopLoss,
        takeProfitPercentage: strategies.conservative.takeProfit,
        trailingStopEnabled: strategies.conservative.trailing,
        positionSize: existingPosition ? 0 : 0.1, // No initial purchase for existing positions
        reentryEnabled: strategies.conservative.reentry,
        initialPurchase: !existingPosition, // Only purchase if no existing position
        walletId: activeWallet?.wallet_type === 'platform' ? activeWallet.id.toString() :
          platformWallets.length > 0 ? platformWallets[0].id.toString() : '',
        // Advanced trading features
        advancedFeaturesEnabled: false,
        multipleTakeProfits: [
          { percentage: 15, positionPercentage: 30 },
          { percentage: 30, positionPercentage: 40 },
          { percentage: 50, positionPercentage: 30 }
        ],
        dynamicStopLossEnabled: false,
        atrMultiplier: 2.0,
        partialTradingEnabled: false,
        timeBasedExitEnabled: false,
        timeExitDurationHours: 24,
        volumeSpikeDetection: false,
        volumeSpikeThreshold: 200,
        priceTargetsEnabled: false,
        priceTargets: [],
        // Risk management parameters
        maxCapitalPerTrade: 10,
        emergencyStopEnabled: true,
        maxDrawdownProtection: true,
        // Risk management parameters
        maxCapitalPerTrade: 10,
        emergencyStopEnabled: true,
        maxDrawdownProtection: true
      };

      setParameters(initialParams);

      // Pre-fill token information from discovery context
      setTokenInfo({
        symbol: token.symbol,
        name: token.name,
        address: token.address,
        priceUSD: token.price,
        marketCap: token.marketCap,
        imageUrl: token.imageUrl || token.logo,
        isVerified: token.isVerified || false
      });

      fetchWalletBalances();
    }
  }, [isOpen, token?.address, existingPosition?.id, activeWallet?.id, platformWallets.length]);

  // Update parameters when strategy changes
  useEffect(() => {
    if (strategy && strategies[strategy]) {
      const strategyConfig = strategies[strategy];
      setParameters(prev => ({
        ...prev,
        stopLossPercentage: strategyConfig.stopLoss,
        takeProfitPercentage: strategyConfig.takeProfit,
        trailingStopEnabled: strategyConfig.trailing,
        reentryEnabled: strategyConfig.reentry
      }));
    }
  }, [strategy]);

  // Fetch wallet balances
  const fetchWalletBalances = useCallback(async () => {
    if (!isAuthenticated || platformWallets.length === 0) return;

    const balances = {};

    for (const wallet of platformWallets) {
      try {
        const response = await makeAuthenticatedRequest(`/wallets/${wallet.id}/balance`);
        if (response.success) {
          balances[wallet.id] = response.data.balance;
        }
      } catch (error) {
        console.warn(`Could not fetch balance for wallet ${wallet.id}:`, error.message);
        balances[wallet.id] = { sol: 0 };
      }
    }

    setWalletBalances(balances);
  }, [isAuthenticated, platformWallets, tokens?.access_token]);

  // Get price quote for initial purchase
  const updatePriceQuote = useCallback(async () => {
    if (!parameters.initialPurchase || !parameters.positionSize || !selectedWallet || !token) return;

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/trading/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inputMint: 'So11111111111111111111111111111111111111112', // SOL
          outputMint: token.address,
          amount: Math.floor(parameters.positionSize * 1000000000), // Convert SOL to lamports
          slippageBps: 50 // 0.5% slippage
        })
      });

      const quoteData = await response.json();
      if (quoteData.success) {
        setPriceQuote(quoteData.data.quote);

        // Calculate trading fees
        const feesResponse = await fetch(`${API_BASE_URL}/token-info/trading-fees`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            inputMint: 'So11111111111111111111111111111111111111112',
            outputMint: token.address,
            amount: parameters.positionSize
          })
        });

        const feesData = await feesResponse.json();
        if (feesData.success) {
          setTradingFees(feesData.data.fees);
        }
      }
    } catch (error) {
      console.warn('Could not get price quote:', error.message);
    } finally {
      setLoading(false);
    }
  }, [parameters.initialPurchase, parameters.positionSize, selectedWallet?.id, token?.address, API_BASE_URL]);

  // Update quote when parameters change (debounced)
  useEffect(() => {
    if (currentStep !== 'parameters' || !parameters.initialPurchase) {
      return;
    }

    const timeoutId = setTimeout(() => {
      updatePriceQuote();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [parameters.initialPurchase, parameters.positionSize, selectedWallet?.id, token?.address, currentStep]);

  // Create bot session
  const createBotSession = async () => {
    if (!selectedWallet) return;

    setCurrentStep('creating');
    setLoading(true);

    try {
      const endpoint = existingPosition ? '/trading/add-existing-to-bot' : '/trading/add-to-bot';

      const result = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          tokenAddress: token.address,
          walletId: selectedWallet.id,
          strategy: strategy,
          parameters: {
            stopLossPercentage: parameters.stopLossPercentage,
            takeProfitPercentage: parameters.takeProfitPercentage,
            trailingStopEnabled: parameters.trailingStopEnabled,
            positionSize: parameters.positionSize,
            reentryEnabled: parameters.reentryEnabled,
            initialPurchase: parameters.initialPurchase
          },
          advancedOptions: parameters.advancedFeaturesEnabled ? {
            multipleTakeProfits: parameters.multipleTakeProfits,
            dynamicStopLossEnabled: parameters.dynamicStopLossEnabled,
            atrMultiplier: parameters.atrMultiplier,
            partialTradingEnabled: parameters.partialTradingEnabled,
            timeBasedExitEnabled: parameters.timeBasedExitEnabled,
            timeExitDurationHours: parameters.timeExitDurationHours,
            volumeSpikeDetection: parameters.volumeSpikeDetection,
            volumeSpikeThreshold: parameters.volumeSpikeThreshold,
            priceTargetsEnabled: parameters.priceTargetsEnabled,
            priceTargets: parameters.priceTargets
          } : {},
          useCurrentPriceAsBaseline: !!existingPosition,
          source: 'discovery' // Track source for analytics
        })
      });

      if (result.success) {
        showSuccess(`Bot configured successfully for ${token.symbol}!`);
        handleClose();
      } else {
        throw new Error(result.error?.message || 'Bot configuration failed');
      }
    } catch (error) {
      showError(`Bot configuration failed: ${error.message}`);
      setCurrentStep('confirmation');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (currentStep === 'creating') return; // Prevent closing during creation

    setCurrentStep('strategy');
    setStrategy('conservative');
    setParameters({
      stopLossPercentage: 10,
      takeProfitPercentage: 25,
      trailingStopEnabled: true,
      positionSize: 0.1,
      reentryEnabled: true,
      initialPurchase: true,
      walletId: ''
    });
    setPriceQuote(null);
    setTradingFees(null);
    onClose();
  };

  // Step navigation
  const goToParameters = () => {
    setCurrentStep('parameters');
  };

  const goToAdvanced = () => {
    // Validate parameters
    if (!parameters.walletId) {
      showError('Please select a trading wallet');
      return;
    }

    if (parameters.initialPurchase && parameters.positionSize <= 0) {
      showError('Please enter a valid position size');
      return;
    }

    if (parameters.stopLossPercentage <= 0 || parameters.stopLossPercentage >= 100) {
      showError('Stop-loss percentage must be between 0 and 100');
      return;
    }

    if (parameters.takeProfitPercentage <= 0) {
      showError('Take-profit percentage must be greater than 0');
      return;
    }

    setCurrentStep('advanced');
  };

  const goToConfirmation = () => {
    // Validate advanced parameters if enabled
    if (parameters.advancedFeaturesEnabled) {
      if (parameters.multipleTakeProfits.length === 0) {
        showError('Please configure at least one take-profit level');
        return;
      }

      const totalPercentage = parameters.multipleTakeProfits.reduce((sum, tp) => sum + tp.positionPercentage, 0);
      if (totalPercentage > 100) {
        showError('Total position percentage for take-profits cannot exceed 100%');
        return;
      }

      if (parameters.dynamicStopLossEnabled && (parameters.atrMultiplier <= 0 || parameters.atrMultiplier > 10)) {
        showError('ATR multiplier must be between 0 and 10');
        return;
      }

      if (parameters.timeBasedExitEnabled && (parameters.timeExitDurationHours <= 0 || parameters.timeExitDurationHours > 168)) {
        showError('Time exit duration must be between 1 and 168 hours (1 week)');
        return;
      }

      if (parameters.volumeSpikeDetection && (parameters.volumeSpikeThreshold < 100 || parameters.volumeSpikeThreshold > 1000)) {
        showError('Volume spike threshold must be between 100% and 1000%');
        return;
      }
    }

    setCurrentStep('confirmation');
  };

  const goBackToStrategy = () => {
    setCurrentStep('strategy');
  };

  const goBackToParameters = () => {
    setCurrentStep('parameters');
  };

  const goBackToAdvanced = () => {
    setCurrentStep('advanced');
  };

  if (!isOpen) return null;

  return (
    <div className="bot-config-modal-overlay" onClick={handleClose}>
      <div className="bot-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="bot-config-modal-header">
          <div className="modal-title">
            <span className="bot-icon">🤖</span>
            <div className="title-content">
              <h3>Configure Trading Bot</h3>
              <div className="token-info" style={{
                display: 'flex', flexDirection: 'row'
              }}>
                <img
                  src={tokenInfo?.imageUrl}
                  alt={tokenInfo?.name}
                  className="token-logo-small"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/24x24/6366f1/ffffff?text=${tokenInfo?.symbol?.charAt(0) || '?'}`;
                  }}
                />
                <div className="token-details">
                  <span className="token-symbol">{tokenInfo?.symbol}</span>
                  <span className="token-name">{tokenInfo?.name}</span>
                </div>
              </div>
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={currentStep === 'creating'}
          >
            ×
          </button>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator bot-config" style={{
          marginLeft: '17%', gap: 10
        }}>
          <div className={`step ${currentStep === 'strategy' ? 'active' : currentStep !== 'strategy' ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Strategy</span>
          </div>
          <div className={`step ${currentStep === 'parameters' ? 'active' : (currentStep === 'advanced' || currentStep === 'confirmation' || currentStep === 'creating') ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Parameters</span>
          </div>
          <div className={`step ${currentStep === 'advanced' ? 'active' : (currentStep === 'confirmation' || currentStep === 'creating') ? 'completed' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Advanced</span>
          </div>
          <div className={`step ${currentStep === 'confirmation' ? 'active' : currentStep === 'creating' ? 'completed' : ''}`}>
            <span className="step-number">4</span>
            <span className="step-label">Confirm</span>
          </div>
        </div>

        {/* Modal Content */}
        <div className="bot-config-modal-content">
          {currentStep === 'strategy' && (
            <StrategySelectionStep
              strategy={strategy}
              setStrategy={setStrategy}
              strategies={strategies}
              existingPosition={existingPosition}
              onNext={goToParameters}
            />
          )}

          {currentStep === 'parameters' && (
            <ParameterConfigurationStep
              parameters={parameters}
              setParameters={setParameters}
              strategy={strategy}
              strategies={strategies}
              platformWallets={platformWallets}
              walletBalances={walletBalances}
              selectedWallet={selectedWallet}
              token={token}
              tokenInfo={tokenInfo}
              existingPosition={existingPosition}
              priceQuote={priceQuote}
              tradingFees={tradingFees}
              onBack={goBackToStrategy}
              onNext={goToAdvanced}
              loading={loading}
            />
          )}

          {currentStep === 'advanced' && (
            <AdvancedFeaturesStep
              parameters={parameters}
              setParameters={setParameters}
              strategy={strategy}
              strategies={strategies}
              token={token}
              tokenInfo={tokenInfo}
              onBack={goBackToParameters}
              onNext={goToConfirmation}
            />
          )}

          {currentStep === 'confirmation' && (
            <ConfirmationStep
              parameters={parameters}
              strategy={strategy}
              strategies={strategies}
              selectedWallet={selectedWallet}
              token={token}
              tokenInfo={tokenInfo}
              existingPosition={existingPosition}
              priceQuote={priceQuote}
              tradingFees={tradingFees}
              onBack={goBackToAdvanced}
              onCreate={createBotSession}
              loading={loading}
            />
          )}

          {currentStep === 'creating' && (
            <CreatingStep
              strategy={strategy}
              strategies={strategies}
              token={token}
              existingPosition={existingPosition}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Advanced Features Step Component
const AdvancedFeaturesStep = ({
  parameters,
  setParameters,
  strategy,
  strategies,
  token,
  tokenInfo,
  onBack,
  onNext
}) => {
  const strategyConfig = strategies[strategy];

  const updateParameter = (key, value) => {
    setParameters(prev => ({ ...prev, [key]: value }));
  };

  const updateTakeProfitLevel = (index, field, value) => {
    const newLevels = [...parameters.multipleTakeProfits];
    newLevels[index] = { ...newLevels[index], [field]: value };
    updateParameter('multipleTakeProfits', newLevels);
  };

  const addTakeProfitLevel = () => {
    const newLevels = [...parameters.multipleTakeProfits];
    const totalUsed = newLevels.reduce((sum, level) => sum + level.positionPercentage, 0);
    const remainingPercentage = Math.max(10, 100 - totalUsed);
    newLevels.push({
      percentage: 25,
      positionPercentage: Math.min(remainingPercentage, 25)
    });
    updateParameter('multipleTakeProfits', newLevels);
  };

  const removeTakeProfitLevel = (index) => {
    const newLevels = parameters.multipleTakeProfits.filter((_, i) => i !== index);
    updateParameter('multipleTakeProfits', newLevels);
  };

  const addPriceTarget = () => {
    const newTargets = [...parameters.priceTargets];
    newTargets.push({
      type: 'buy',
      price: tokenInfo?.priceUSD || 0,
      amount: 0.1,
      amountType: 'fixed'
    });
    updateParameter('priceTargets', newTargets);
  };

  const updatePriceTarget = (index, field, value) => {
    const newTargets = [...parameters.priceTargets];
    newTargets[index] = { ...newTargets[index], [field]: value };
    updateParameter('priceTargets', newTargets);
  };

  const removePriceTarget = (index) => {
    const newTargets = parameters.priceTargets.filter((_, i) => i !== index);
    updateParameter('priceTargets', newTargets);
  };

  // Calculate total position percentage for validation
  const totalTakeProfitPercentage = parameters.multipleTakeProfits.reduce(
    (sum, level) => sum + level.positionPercentage, 0
  );

  // Risk management validation
  const validateRiskParameters = () => {
    const errors = [];

    if (parameters.advancedFeaturesEnabled) {
      if (totalTakeProfitPercentage > 100) {
        errors.push('Total take-profit position percentage cannot exceed 100%');
      }

      if (parameters.dynamicStopLossEnabled && parameters.atrMultiplier > 5) {
        errors.push('ATR multiplier above 5x is extremely risky');
      }

      if (parameters.timeBasedExitEnabled && parameters.timeExitDurationHours < 1) {
        errors.push('Time-based exit duration must be at least 1 hour');
      }
    }

    return errors;
  };

  const riskErrors = validateRiskParameters();

  return (
    <div className="advanced-features-step">
      <div className="step-header">
        <h4>Advanced Trading Features</h4>
        <p>Configure advanced trading logic for sophisticated strategies</p>
      </div>

      {/* Enable Advanced Features Toggle */}
      <div className="form-section">
        <div className="form-section-header">
          <label className="form-label">Advanced Features</label>
          <div className="toggle-switch">
            <input
              type="checkbox"
              id="advancedFeaturesEnabled"
              checked={parameters.advancedFeaturesEnabled}
              onChange={(e) => updateParameter('advancedFeaturesEnabled', e.target.checked)}
            />
            <label htmlFor="advancedFeaturesEnabled" className="toggle-label">
              {parameters.advancedFeaturesEnabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>
        <div className="parameter-help">
          Enable advanced trading features for more sophisticated strategies
        </div>
      </div>

      {parameters.advancedFeaturesEnabled && (
        <>
          {/* Enhanced Multiple Take-Profit Levels */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Multi-Level Take-Profit Configuration</label>
              <div className="position-usage-indicator">
                <span className="usage-text">Position Usage: </span>
                <span className={`usage-value ${totalTakeProfitPercentage > 100 ? 'error' : totalTakeProfitPercentage > 90 ? 'warning' : 'success'}`}>
                  {totalTakeProfitPercentage}%
                </span>
              </div>
            </div>

            <div className="take-profit-levels enhanced">
              {parameters.multipleTakeProfits.map((level, index) => (
                <div key={index} className="take-profit-level enhanced">
                  <div className="level-header">
                    <div className="level-info">
                      <span className="level-number">Level {index + 1}</span>
                      <span className="level-preview">
                        {level.percentage}% profit → {level.positionPercentage}% position
                      </span>
                    </div>
                    {parameters.multipleTakeProfits.length > 1 && (
                      <button
                        type="button"
                        className="remove-level-btn"
                        onClick={() => removeTakeProfitLevel(index)}
                        title="Remove this take-profit level"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="level-controls">
                    <div className="slider-control">
                      <label className="sub-label">Profit Target: {level.percentage}%</label>
                      <div className="visual-slider-container">
                        <input
                          type="range"
                          min="5"
                          max="500"
                          step="5"
                          value={level.percentage}
                          onChange={(e) => updateTakeProfitLevel(index, 'percentage', parseFloat(e.target.value))}
                          className="visual-slider profit-slider"
                        />
                        <div className="slider-markers">
                          <span className="marker">5%</span>
                          <span className="marker">50%</span>
                          <span className="marker">100%</span>
                          <span className="marker">500%</span>
                        </div>
                      </div>
                    </div>

                    <div className="slider-control">
                      <label className="sub-label">Position Size: {level.positionPercentage}%</label>
                      <div className="visual-slider-container">
                        <input
                          type="range"
                          min="5"
                          max="100"
                          step="5"
                          value={level.positionPercentage}
                          onChange={(e) => updateTakeProfitLevel(index, 'positionPercentage', parseFloat(e.target.value))}
                          className="visual-slider position-slider"
                        />
                        <div className="slider-markers">
                          <span className="marker">5%</span>
                          <span className="marker">25%</span>
                          <span className="marker">50%</span>
                          <span className="marker">100%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="add-level-btn enhanced"
                onClick={addTakeProfitLevel}
                disabled={parameters.multipleTakeProfits.length >= 5 || totalTakeProfitPercentage >= 100}
              >
                <span className="btn-icon">+</span>
                Add Take-Profit Level
                {parameters.multipleTakeProfits.length >= 5 && (
                  <span className="btn-limit">(Max 5 levels)</span>
                )}
              </button>
            </div>

            <div className="parameter-help enhanced">
              <div className="help-content">
                <span className="help-icon">💡</span>
                <div className="help-text">
                  <strong>Multi-level take-profits</strong> allow gradual position exits at different profit targets.
                  Each level sells a percentage of your position when the profit target is reached.
                </div>
              </div>
              {totalTakeProfitPercentage > 100 && (
                <div className="validation-error">
                  <span className="error-icon">⚠️</span>
                  Total position percentage cannot exceed 100%. Please adjust your levels.
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Dynamic Stop-Loss */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Dynamic Stop-Loss Configuration</label>
              <div className="toggle-switch enhanced">
                <input
                  type="checkbox"
                  id="dynamicStopLossEnabled"
                  checked={parameters.dynamicStopLossEnabled}
                  onChange={(e) => updateParameter('dynamicStopLossEnabled', e.target.checked)}
                />
                <label htmlFor="dynamicStopLossEnabled" className="toggle-label">
                  <span className="toggle-text">
                    {parameters.dynamicStopLossEnabled ? 'ATR-Based' : 'Fixed Percentage'}
                  </span>
                </label>
              </div>
            </div>

            {parameters.dynamicStopLossEnabled ? (
              <div className="dynamic-stop-loss-config">
                <div className="volatility-options">
                  <label className="sub-label">Volatility Sensitivity</label>
                  <div className="volatility-presets">
                    {[
                      { label: 'Conservative', value: 1.0, desc: 'Tight stops, less volatility tolerance' },
                      { label: 'Moderate', value: 2.0, desc: 'Balanced approach to market volatility' },
                      { label: 'Aggressive', value: 3.5, desc: 'Wide stops, high volatility tolerance' }
                    ].map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        className={`volatility-preset ${parameters.atrMultiplier === preset.value ? 'active' : ''}`}
                        onClick={() => updateParameter('atrMultiplier', preset.value)}
                      >
                        <span className="preset-label">{preset.label}</span>
                        <span className="preset-value">{preset.value}x ATR</span>
                        <span className="preset-desc">{preset.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="atr-multiplier-control">
                  <label className="sub-label">Custom ATR Multiplier: {parameters.atrMultiplier.toFixed(1)}x</label>
                  <div className="visual-slider-container">
                    <input
                      type="range"
                      min="0.5"
                      max="5"
                      step="0.1"
                      value={parameters.atrMultiplier}
                      onChange={(e) => updateParameter('atrMultiplier', parseFloat(e.target.value))}
                      className="visual-slider atr-slider"
                    />
                    <div className="slider-markers">
                      <span className="marker">0.5x</span>
                      <span className="marker">1.5x</span>
                      <span className="marker">3x</span>
                      <span className="marker">5x</span>
                    </div>
                  </div>
                  <div className="atr-explanation">
                    <div className="risk-indicator">
                      <span className="risk-level">
                        Risk Level:
                        <span className={`risk-value ${parameters.atrMultiplier <= 1.5 ? 'low' :
                          parameters.atrMultiplier <= 3 ? 'medium' : 'high'
                          }`}>
                          {parameters.atrMultiplier <= 1.5 ? 'Low' :
                            parameters.atrMultiplier <= 3 ? 'Medium' : 'High'}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="fixed-stop-loss-info">
                <div className="info-card">
                  <span className="info-icon">📊</span>
                  <div className="info-content">
                    <strong>Fixed Stop-Loss Active</strong>
                    <p>Using {parameters.stopLossPercentage}% fixed stop-loss from basic parameters</p>
                  </div>
                </div>
              </div>
            )}

            <div className="parameter-help enhanced">
              <div className="help-content">
                <span className="help-icon">🎯</span>
                <div className="help-text">
                  <strong>Dynamic stop-loss</strong> adjusts based on market volatility using Average True Range (ATR).
                  Higher multipliers provide more room for normal price fluctuations but increase potential losses.
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Partial Trading */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Partial Position Trading</label>
              <div className="toggle-switch enhanced">
                <input
                  type="checkbox"
                  id="partialTradingEnabled"
                  checked={parameters.partialTradingEnabled}
                  onChange={(e) => updateParameter('partialTradingEnabled', e.target.checked)}
                />
                <label htmlFor="partialTradingEnabled" className="toggle-label">
                  <span className="toggle-text">
                    {parameters.partialTradingEnabled ? 'Gradual Trading' : 'All-or-Nothing'}
                  </span>
                </label>
              </div>
            </div>

            {parameters.partialTradingEnabled && (
              <div className="partial-trading-config">
                <div className="trading-strategy-options">
                  <div className="strategy-option">
                    <div className="option-header">
                      <span className="option-icon">📈</span>
                      <span className="option-title">Entry Strategy</span>
                    </div>
                    <div className="option-description">
                      Split initial purchases into 2-3 smaller orders to reduce impact and improve average entry price
                    </div>
                  </div>

                  <div className="strategy-option">
                    <div className="option-header">
                      <span className="option-icon">📉</span>
                      <span className="option-title">Exit Strategy</span>
                    </div>
                    <div className="option-description">
                      Execute take-profits and stop-losses in smaller chunks to minimize slippage
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="parameter-help enhanced">
              <div className="help-content">
                <span className="help-icon">⚡</span>
                <div className="help-text">
                  <strong>Partial trading</strong> reduces market impact by splitting large orders into smaller chunks.
                  This can improve execution prices but may result in partial fills during volatile markets.
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Time-Based Exit */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Time-Based Exit System</label>
              <div className="toggle-switch enhanced">
                <input
                  type="checkbox"
                  id="timeBasedExitEnabled"
                  checked={parameters.timeBasedExitEnabled}
                  onChange={(e) => updateParameter('timeBasedExitEnabled', e.target.checked)}
                />
                <label htmlFor="timeBasedExitEnabled" className="toggle-label">
                  <span className="toggle-text">
                    {parameters.timeBasedExitEnabled ? 'Time Limits Active' : 'No Time Limits'}
                  </span>
                </label>
              </div>
            </div>

            {parameters.timeBasedExitEnabled && (
              <div className="time-exit-config">
                <div className="timeframe-presets">
                  <label className="sub-label">Quick Timeframes</label>
                  <div className="preset-buttons">
                    {[
                      { label: '1 Hour', value: 1, desc: 'Scalping' },
                      { label: '4 Hours', value: 4, desc: 'Short-term' },
                      { label: '24 Hours', value: 24, desc: 'Day trading' },
                      { label: '72 Hours', value: 72, desc: 'Swing trading' },
                      { label: '1 Week', value: 168, desc: 'Position trading' }
                    ].map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        className={`timeframe-preset ${parameters.timeExitDurationHours === preset.value ? 'active' : ''}`}
                        onClick={() => updateParameter('timeExitDurationHours', preset.value)}
                      >
                        <span className="preset-label">{preset.label}</span>
                        <span className="preset-desc">{preset.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="custom-duration-control">
                  <label className="sub-label">
                    Custom Duration: {parameters.timeExitDurationHours}h
                    ({Math.round(parameters.timeExitDurationHours / 24 * 10) / 10} days)
                  </label>
                  <div className="visual-slider-container">
                    <input
                      type="range"
                      min="1"
                      max="168"
                      step="1"
                      value={parameters.timeExitDurationHours}
                      onChange={(e) => updateParameter('timeExitDurationHours', parseInt(e.target.value))}
                      className="visual-slider duration-slider"
                    />
                    <div className="slider-markers">
                      <span className="marker">1h</span>
                      <span className="marker">24h</span>
                      <span className="marker">72h</span>
                      <span className="marker">168h</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="parameter-help enhanced">
              <div className="help-content">
                <span className="help-icon">⏰</span>
                <div className="help-text">
                  <strong>Time-based exits</strong> automatically close positions that haven't reached profit targets
                  within the specified timeframe. This prevents capital from being tied up in stagnant positions.
                </div>
              </div>
            </div>
          </div>

          {/* Volume Spike Detection */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Volume Spike Detection</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="volumeSpikeDetection"
                  checked={parameters.volumeSpikeDetection}
                  onChange={(e) => updateParameter('volumeSpikeDetection', e.target.checked)}
                />
                <label htmlFor="volumeSpikeDetection" className="toggle-label">
                  {parameters.volumeSpikeDetection ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>

            {parameters.volumeSpikeDetection && (
              <div className="parameter-input">
                <label className="sub-label">Spike Threshold (%)</label>
                <div className="input-with-slider">
                  <input
                    type="range"
                    min="100"
                    max="1000"
                    step="50"
                    value={parameters.volumeSpikeThreshold}
                    onChange={(e) => updateParameter('volumeSpikeThreshold', parseInt(e.target.value))}
                    className="parameter-slider"
                  />
                  <div className="slider-value">{parameters.volumeSpikeThreshold}%</div>
                </div>
                <div className="parameter-help">
                  Trigger early entry/exit signals when volume increases by this percentage
                </div>
              </div>
            )}

            <div className="parameter-help">
              {parameters.volumeSpikeDetection
                ? "Use volume spikes as early signals for entry and exit decisions"
                : "Ignore volume data and rely only on price-based signals"
              }
            </div>
          </div>

          {/* Price Targets */}
          <div className="form-section">
            <div className="form-section-header">
              <label className="form-label">Price Targets</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="priceTargetsEnabled"
                  checked={parameters.priceTargetsEnabled}
                  onChange={(e) => updateParameter('priceTargetsEnabled', e.target.checked)}
                />
                <label htmlFor="priceTargetsEnabled" className="toggle-label">
                  {parameters.priceTargetsEnabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>

            {parameters.priceTargetsEnabled && (
              <div className="price-targets">
                {parameters.priceTargets.map((target, index) => (
                  <div key={index} className="price-target">
                    <div className="target-header">
                      <span className="target-number">Target {index + 1}</span>
                      <button
                        type="button"
                        className="remove-target-btn"
                        onClick={() => removePriceTarget(index)}
                      >
                        ×
                      </button>
                    </div>

                    <div className="target-inputs">
                      <div className="input-group">
                        <label className="sub-label">Type</label>
                        <select
                          className="form-select"
                          value={target.type}
                          onChange={(e) => updatePriceTarget(index, 'type', e.target.value)}
                        >
                          <option value="buy">Buy</option>
                          <option value="sell">Sell</option>
                        </select>
                      </div>

                      <div className="input-group">
                        <label className="sub-label">Price ($)</label>
                        <input
                          type="number"
                          className="form-input"
                          value={target.price}
                          onChange={(e) => updatePriceTarget(index, 'price', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.000001"
                        />
                      </div>

                      <div className="input-group">
                        <label className="sub-label">Amount</label>
                        <input
                          type="number"
                          className="form-input"
                          value={target.amount}
                          onChange={(e) => updatePriceTarget(index, 'amount', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.001"
                        />
                      </div>

                      <div className="input-group">
                        <label className="sub-label">Type</label>
                        <select
                          className="form-select"
                          value={target.amountType}
                          onChange={(e) => updatePriceTarget(index, 'amountType', e.target.value)}
                        >
                          <option value="fixed">Fixed Amount</option>
                          <option value="percentage">Percentage</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="add-target-btn"
                  onClick={addPriceTarget}
                >
                  + Add Price Target
                </button>
              </div>
            )}

            <div className="parameter-help">
              {parameters.priceTargetsEnabled
                ? "Set specific buy/sell orders that execute when target prices are reached"
                : "No price targets - rely on percentage-based take-profit and stop-loss"
              }
            </div>
          </div>
        </>
      )}

      {/* Risk Management Section */}
      <div className="form-section risk-management">
        <div className="form-section-header">
          <label className="form-label">Risk Management & Safety Controls</label>
          <div className="risk-status-indicator">
            <span className={`risk-badge ${riskErrors.length > 0 ? 'high-risk' :
              (parameters.dynamicStopLossEnabled && parameters.atrMultiplier > 3) ||
                totalTakeProfitPercentage > 80 ? 'medium-risk' : 'low-risk'
              }`}>
              {riskErrors.length > 0 ? 'High Risk' :
                (parameters.dynamicStopLossEnabled && parameters.atrMultiplier > 3) ||
                  totalTakeProfitPercentage > 80 ? 'Medium Risk' : 'Low Risk'}
            </span>
          </div>
        </div>

        <div className="risk-controls">
          <div className="capital-limits">
            <label className="sub-label">Maximum Capital Per Trade</label>
            <div className="capital-limit-options">
              <div className="limit-option">
                <input
                  type="radio"
                  id="capitalLimit5"
                  name="capitalLimit"
                  checked={parameters.maxCapitalPerTrade === 5}
                  onChange={() => updateParameter('maxCapitalPerTrade', 5)}
                />
                <label htmlFor="capitalLimit5" className="limit-label">
                  <span className="limit-value">5%</span>
                  <span className="limit-desc">Conservative</span>
                </label>
              </div>

              <div className="limit-option">
                <input
                  type="radio"
                  id="capitalLimit10"
                  name="capitalLimit"
                  checked={parameters.maxCapitalPerTrade === 10}
                  onChange={() => updateParameter('maxCapitalPerTrade', 10)}
                />
                <label htmlFor="capitalLimit10" className="limit-label">
                  <span className="limit-value">10%</span>
                  <span className="limit-desc">Moderate</span>
                </label>
              </div>

              <div className="limit-option">
                <input
                  type="radio"
                  id="capitalLimit20"
                  name="capitalLimit"
                  checked={parameters.maxCapitalPerTrade === 20}
                  onChange={() => updateParameter('maxCapitalPerTrade', 20)}
                />
                <label htmlFor="capitalLimit20" className="limit-label">
                  <span className="limit-value">20%</span>
                  <span className="limit-desc">Aggressive</span>
                </label>
              </div>
            </div>
          </div>

          <div className="safety-controls">
            <div className="safety-toggle">
              <input
                type="checkbox"
                id="emergencyStopEnabled"
                checked={parameters.emergencyStopEnabled || false}
                onChange={(e) => updateParameter('emergencyStopEnabled', e.target.checked)}
              />
              <label htmlFor="emergencyStopEnabled" className="safety-label">
                <span className="safety-icon">🛑</span>
                <div className="safety-text">
                  <strong>Emergency Stop</strong>
                  <span>Halt all trading if losses exceed 50% of position</span>
                </div>
              </label>
            </div>

            <div className="safety-toggle">
              <input
                type="checkbox"
                id="maxDrawdownProtection"
                checked={parameters.maxDrawdownProtection || false}
                onChange={(e) => updateParameter('maxDrawdownProtection', e.target.checked)}
              />
              <label htmlFor="maxDrawdownProtection" className="safety-label">
                <span className="safety-icon">📉</span>
                <div className="safety-text">
                  <strong>Drawdown Protection</strong>
                  <span>Reduce position sizes after consecutive losses</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        {riskErrors.length > 0 && (
          <div className="risk-errors">
            <div className="error-header">
              <span className="error-icon">⚠️</span>
              <strong>Risk Configuration Issues</strong>
            </div>
            <ul className="error-list">
              {riskErrors.map((error, index) => (
                <li key={index} className="error-item">{error}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Enhanced Advanced Features Summary */}
      {parameters.advancedFeaturesEnabled && (
        <div className="advanced-summary enhanced">
          <div className="summary-header">
            <h5>Configuration Summary</h5>
            <div className="complexity-indicator">
              <span className="complexity-label">Complexity:</span>
              <span className={`complexity-value ${(parameters.multipleTakeProfits.length > 3 ||
                parameters.dynamicStopLossEnabled ||
                parameters.timeBasedExitEnabled) ? 'high' :
                (parameters.multipleTakeProfits.length > 1 ||
                  parameters.partialTradingEnabled) ? 'medium' : 'low'
                }`}>
                {(parameters.multipleTakeProfits.length > 3 ||
                  parameters.dynamicStopLossEnabled ||
                  parameters.timeBasedExitEnabled) ? 'High' :
                  (parameters.multipleTakeProfits.length > 1 ||
                    parameters.partialTradingEnabled) ? 'Medium' : 'Low'}
              </span>
            </div>
          </div>

          <div className="summary-grid">
            <div className="summary-card">
              <span className="card-icon">🎯</span>
              <div className="card-content">
                <strong>Take-Profit Strategy</strong>
                <span>{parameters.multipleTakeProfits.length} levels using {totalTakeProfitPercentage}% of position</span>
              </div>
            </div>

            <div className="summary-card">
              <span className="card-icon">🛡️</span>
              <div className="card-content">
                <strong>Stop-Loss Method</strong>
                <span>
                  {parameters.dynamicStopLossEnabled
                    ? `Dynamic ATR (${parameters.atrMultiplier}x)`
                    : `Fixed ${parameters.stopLossPercentage}%`}
                </span>
              </div>
            </div>

            {parameters.timeBasedExitEnabled && (
              <div className="summary-card">
                <span className="card-icon">⏰</span>
                <div className="card-content">
                  <strong>Time Limit</strong>
                  <span>{parameters.timeExitDurationHours}h maximum hold time</span>
                </div>
              </div>
            )}

            {parameters.partialTradingEnabled && (
              <div className="summary-card">
                <span className="card-icon">📈</span>
                <div className="card-content">
                  <strong>Execution Style</strong>
                  <span>Gradual entry/exit for better prices</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Risk Warning for Advanced Features */}
      {parameters.advancedFeaturesEnabled && (
        <div className="risk-warning advanced">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <strong>Advanced Features Risk Warning</strong>
            <p>
              Advanced trading features increase complexity and may result in unexpected behavior.
              Ensure you understand each feature before enabling. Start with small position sizes
              to test advanced strategies.
            </p>
          </div>
        </div>
      )}

      <div className="step-actions">
        <button
          className="btn btn-secondary"
          onClick={onBack}
        >
          ← Back to Parameters
        </button>

        <button
          className="btn btn-primary"
          onClick={onNext}
        >
          Continue to Confirmation →
        </button>
      </div>
    </div>
  );
};

export default BotConfigModal;

// Strategy Selection Step Component
const StrategySelectionStep = ({
  strategy,
  setStrategy,
  strategies,
  existingPosition,
  onNext
}) => {
  const handleStrategySelect = (key) => {
    setStrategy(key);
    // Auto-advance to next step after a short delay to show selection
    setTimeout(() => {
      onNext();
    }, 300);
  };

  return (
    <div className="strategy-selection-step">
      <div className="step-header">
        <h4>Choose Trading Strategy</h4>
        <p>
          {existingPosition
            ? `Select a strategy to monitor your existing ${existingPosition.symbol} position`
            : 'Select a trading strategy template that matches your risk tolerance'
          }
        </p>
      </div>

      <div className="strategies-grid">
        {Object.entries(strategies).map(([key, strategyConfig]) => (
          <div
            key={key}
            className={`strategy-card ${strategy === key ? 'selected' : ''}`}
            onClick={() => handleStrategySelect(key)}
            style={{ '--strategy-color': strategyConfig.color }}
          >
            <div className="strategy-header">
              <span className="strategy-icon">{strategyConfig.icon}</span>
              <h5>{strategyConfig.name}</h5>
            </div>

            <p className="strategy-description">{strategyConfig.description}</p>

            <div className="strategy-params">
              <div className="param">
                <span className="param-label">Stop-Loss:</span>
                <span className="param-value">{strategyConfig.stopLoss}%</span>
              </div>
              <div className="param">
                <span className="param-label">Take-Profit:</span>
                <span className="param-value">{strategyConfig.takeProfit}%</span>
              </div>
              <div className="param">
                <span className="param-label">Trailing:</span>
                <span className="param-value">{strategyConfig.trailing ? 'Yes' : 'No'}</span>
              </div>
              <div className="param">
                <span className="param-label">Re-entry:</span>
                <span className="param-value">{strategyConfig.reentry ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {strategy === key && (
              <div className="selected-indicator">
                <span className="checkmark">✓</span>
                Selected
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="strategy-explanation">
        <div className="explanation-card">
          <h5>Strategy Features Explained</h5>
          <div className="features-list">
            <div className="feature">
              <span className="feature-icon" style={{ fontSize: 23 }}>🛡️</span>
              <div className="feature-text">
                <strong>Stop-Loss:</strong> Automatically sell when price drops by the specified percentage
              </div>
            </div>
            <div className="feature">
              <span className="feature-icon" style={{ fontSize: 23 }}>📈</span>
              <div className="feature-text">
                <strong>Take-Profit:</strong> Automatically sell when price increases by the target percentage
              </div>
            </div>
            <div className="feature">
              <span className="feature-icon" style={{ fontSize: 23 }}>📊</span>
              <div className="feature-text">
                <strong>Trailing Stop:</strong> Stop-loss follows price up to protect profits
              </div>
            </div>
            <div className="feature">
              <span className="feature-icon" style={{ fontSize: 23 }}>🔄</span>
              <div className="feature-text">
                <strong>Re-entry:</strong> Buy back when price recovers after stop-loss
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="step-actions">
        <div className="auto-advance-hint">
          <span className="hint-icon">💡</span>
          <span className="hint-text">Strategy will be applied automatically when selected</span>
        </div>
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!strategy}
        >
          Configure Parameters →
        </button>
      </div>
    </div>
  );
};

// Parameter Configuration Step Component
const ParameterConfigurationStep = ({
  parameters,
  setParameters,
  strategy,
  strategies,
  platformWallets,
  walletBalances,
  selectedWallet,
  token,
  tokenInfo,
  existingPosition,
  priceQuote,
  tradingFees,
  onBack,
  onNext,
  loading
}) => {
  const strategyConfig = strategies[strategy];

  const updateParameter = (key, value) => {
    setParameters(prev => ({ ...prev, [key]: value }));
  };

  const setPercentageAmount = (percentage) => {
    if (!selectedWallet || !walletBalances[selectedWallet.id]) return;

    const balance = walletBalances[selectedWallet.id];
    const maxAmount = Math.max(0, balance.sol - 0.02); // Reserve for fees
    const amount = (maxAmount * percentage / 100);
    updateParameter('positionSize', parseFloat(amount.toFixed(6)));
  };

  return (
    <div className="parameter-configuration-step">
      <div className="step-header">
        <h4>Configure {strategyConfig.name} Parameters</h4>
        <p>Fine-tune the trading parameters for your strategy</p>
      </div>

      {/* Strategy Summary */}
      <div className="current-strategy-summary">
        <div className="strategy-badge" style={{ backgroundColor: strategyConfig.color }}>
          <span className="strategy-icon">{strategyConfig.icon}</span>
          <span className="strategy-name">{strategyConfig.name}</span>
        </div>
        <button className="change-strategy-btn" onClick={onBack}>
          Change Strategy
        </button>
      </div>

      {/* Wallet Selection */}
      <div className="form-section">
        <label className="form-label">Trading Wallet</label>
        <select
          className="form-select"
          value={parameters.walletId}
          onChange={(e) => updateParameter('walletId', e.target.value)}
        >
          <option value="">Select wallet...</option>
          {platformWallets.map(wallet => (
            <option key={wallet.id} value={wallet.id}>
              {wallet.name || `Wallet ${wallet.id}`} - {walletBalances[wallet.id]?.sol?.toFixed(4) || '0'} SOL
            </option>
          ))}
        </select>

        {selectedWallet && (
          <div className="wallet-details">
            <CopyableAddress
              address={selectedWallet.wallet_address}
              truncate={true}
              truncateLength={8}
            />
            <SafeBalanceDisplay compact={true} walletId={selectedWallet?.id} />
          </div>
        )}
      </div>

      {/* Initial Purchase Configuration (only for new positions) */}
      {!existingPosition && (
        <div className="form-section">
          <div className="form-section-header">
            <label className="form-label">Initial Purchase</label>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="initialPurchase"
                checked={parameters.initialPurchase}
                onChange={(e) => updateParameter('initialPurchase', e.target.checked)}
              />
              <label htmlFor="initialPurchase" className="toggle-label">
                {parameters.initialPurchase ? 'Buy tokens now' : 'Monitor only'}
              </label>
            </div>
          </div>

          {parameters.initialPurchase && (
            <div className="position-size-config">
              <label className="sub-label">Position Size (SOL)</label>
              <div className="position-size-input">
                <input
                  type="number"
                  className="form-input"
                  value={parameters.positionSize}
                  onChange={(e) => updateParameter('positionSize', parseFloat(e.target.value) || 0)}
                  placeholder="0.1"
                  step="0.005"
                  min="0.005"
                />
                <span className="input-suffix">SOL</span>
              </div>

              <div className="percentage-buttons">
                {[25, 50, 75, 100].map(pct => (
                  <button
                    key={pct}
                    className="percentage-btn"
                    onClick={() => setPercentageAmount(pct)}
                    type="button"
                  >
                    {pct === 100 ? 'MAX' : `${pct}%`}
                  </button>
                ))}
              </div>

              {/* Price Quote Display */}
              {priceQuote && tokenInfo && (
                <div className="price-quote-display">
                  <div className="quote-result">
                    <span className="quote-label">Expected tokens:</span>
                    <span className="quote-value">
                      {(priceQuote.outputAmount / 1000000).toFixed(6)} {tokenInfo.symbol}
                    </span>
                  </div>
                  <div className="quote-details">
                    <span className="detail-item">
                      Rate: {((priceQuote.outputAmount / 1000000) / parameters.positionSize).toFixed(2)} {tokenInfo.symbol}/SOL
                    </span>
                    <span className="detail-item">
                      Impact: {(priceQuote.priceImpact || 0).toFixed(2)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Trading Fees */}
              {tradingFees && (
                <div className="trading-fees-display">
                  <span className="fees-label">Est. Trading Fees:</span>
                  <span className="fees-value">{tradingFees.totalFee?.toFixed(6) || '0'} SOL</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Existing Position Info */}
      {existingPosition && (
        <div className="existing-position-info">
          <h5>Existing Position</h5>
          <div className="position-details">
            <div className="detail">
              <span className="label">Balance:</span>
              <span className="value">{existingPosition.balance?.toFixed(6)} {tokenInfo?.symbol}</span>
            </div>
            <div className="detail">
              <span className="label">USD Value:</span>
              <span className="value">${existingPosition.usdValue?.toFixed(2)}</span>
            </div>
            <div className="detail">
              <span className="label">P&L:</span>
              <span className={`value ${(existingPosition.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(existingPosition.pnl || 0) >= 0 ? '+' : ''}{(existingPosition.pnl || 0).toFixed(2)}%
              </span>
            </div>
          </div>
          <p className="position-note">
            The bot will monitor your existing position and execute trades based on current market price.
          </p>
        </div>
      )}

      {/* Stop-Loss Configuration */}
      <div className="form-section">
        <label className="form-label">Stop-Loss Configuration</label>

        <div className="parameter-input">
          <label className="sub-label">Stop-Loss Percentage</label>
          <div className="input-with-slider">
            <input
              type="range"
              min="1"
              max="50"
              value={parameters.stopLossPercentage}
              onChange={(e) => updateParameter('stopLossPercentage', parseInt(e.target.value))}
              className="parameter-slider"
            />
            <div className="slider-value">{parameters.stopLossPercentage}%</div>
          </div>
          <div className="parameter-help">
            Sell when price drops by this percentage from the highest point
          </div>
        </div>

        <div className="parameter-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={parameters.trailingStopEnabled}
              onChange={(e) => updateParameter('trailingStopEnabled', e.target.checked)}
            />
            <span className="toggle-text">Enable Trailing Stop-Loss</span>
          </label>
          <div className="parameter-help">
            {parameters.trailingStopEnabled
              ? "Stop-loss follows price up, protecting profits as price rises"
              : "Stop-loss stays fixed at the initial level"
            }
          </div>
        </div>
      </div>

      {/* Take-Profit Configuration */}
      <div className="form-section">
        <label className="form-label">Take-Profit Configuration</label>

        <div className="parameter-input">
          <label className="sub-label">Take-Profit Percentage</label>
          <div className="input-with-slider">
            <input
              type="range"
              min="5"
              max="200"
              value={parameters.takeProfitPercentage}
              onChange={(e) => updateParameter('takeProfitPercentage', parseInt(e.target.value))}
              className="parameter-slider"
            />
            <div className="slider-value">{parameters.takeProfitPercentage}%</div>
          </div>
          <div className="parameter-help">
            Sell when price increases by this percentage from entry point
          </div>
        </div>
      </div>

      {/* Re-entry Configuration */}
      <div className="form-section">
        <label className="form-label">Re-entry Configuration</label>

        <div className="parameter-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={parameters.reentryEnabled}
              onChange={(e) => updateParameter('reentryEnabled', e.target.checked)}
            />
            <span className="toggle-text">Enable Smart Re-entry</span>
          </label>
          <div className="parameter-help">
            {parameters.reentryEnabled
              ? "Bot will buy back when price recovers after a stop-loss sale"
              : "Bot will stop trading after the first stop-loss sale"
            }
          </div>
        </div>
      </div>

      {/* Risk Warning */}
      <div className="risk-warning">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <strong style={{ color: 'tomato' }}>Risk Warning</strong>
          <p style={{ color: 'tomato' }}>
            Automated trading involves some risk. The bot will execute trades automatically
            based on market conditions. Only invest what you can afford to lose.
          </p>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!parameters.walletId || (parameters.initialPurchase && parameters.positionSize <= 0)}
        >
          Continue to Advanced →
        </button>
      </div>
    </div>
  );
};

// Confirmation Step Component
const ConfirmationStep = ({
  parameters,
  strategy,
  strategies,
  selectedWallet,
  token,
  tokenInfo,
  existingPosition,
  priceQuote,
  tradingFees,
  onBack,
  onCreate,
  loading
}) => {
  const strategyConfig = strategies[strategy];

  return (
    <div className="confirmation-step">
      <div className="step-header">
        <h4>Review Configuration</h4>
        <p>Please review your bot configuration before creating</p>
      </div>

      {/* Configuration Summary */}
      <div className="configuration-summary">
        <div className="summary-section">
          <h5>Strategy</h5>
          <div className="strategy-summary">
            <div className="strategy-badge" style={{ backgroundColor: strategyConfig.color }}>
              <span className="strategy-icon">{strategyConfig.icon}</span>
              <span className="strategy-name">{strategyConfig.name}</span>
            </div>
            <div className="strategy-params-summary">
              <span>Stop-Loss: {parameters.stopLossPercentage}%</span>
              <span>Take-Profit: {parameters.takeProfitPercentage}%</span>
              <span>Trailing: {parameters.trailingStopEnabled ? 'Yes' : 'No'}</span>
              <span>Re-entry: {parameters.reentryEnabled ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <h5>Trading Wallet</h5>
          <div className="wallet-summary">
            <div className="wallet-info">
              <span className="wallet-name">{selectedWallet?.name || `Wallet ${selectedWallet?.id}`}</span>
              <CopyableAddress
                address={selectedWallet?.wallet_address}
                truncate={true}
                truncateLength={8}
              />
            </div>
          </div>
        </div>

        {parameters.initialPurchase && !existingPosition && (
          <div className="summary-section">
            <h5>Initial Purchase</h5>
            <div className="purchase-summary">
              <div className="purchase-details">
                <div className="detail">
                  <span className="label">Amount:</span>
                  <span className="value">{parameters.positionSize} SOL</span>
                </div>
                {priceQuote && (
                  <div className="detail">
                    <span className="label">Expected tokens:</span>
                    <span className="value">
                      {(priceQuote.outputAmount / 1000000).toFixed(6)} {tokenInfo?.symbol}
                    </span>
                  </div>
                )}
                {tradingFees && (
                  <div className="detail">
                    <span className="label">Trading fees:</span>
                    <span className="value">{tradingFees.totalFee?.toFixed(6)} SOL</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {existingPosition && (
          <div className="summary-section">
            <h5>Existing Position</h5>
            <div className="position-summary">
              <div className="detail">
                <span className="label">Balance:</span>
                <span className="value">{existingPosition.balance?.toFixed(6)} {tokenInfo?.symbol}</span>
              </div>
              <div className="detail">
                <span className="label">USD Value:</span>
                <span className="value">${existingPosition.usdValue?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Final Risk Warning */}
      <div className="final-risk-warning">
        <div className="warning-header">
          <span className="warning-icon">⚠️</span>
          <strong>Final Risk Warning</strong>
        </div>
        <div className="warning-content">
          <ul>
            <li>The bot will execute trades automatically based on market conditions</li>
            <li>You may lose some or all of your investment</li>
            <li>Market volatility can trigger rapid buy/sell actions</li>
            <li>Bot performance is not guaranteed and past results don't predict future outcomes</li>
            {parameters.initialPurchase && (
              <li>Initial purchase will be executed immediately upon confirmation</li>
            )}
          </ul>
          <p>
            <strong>By proceeding, you acknowledge these risks and agree to automated trading.</strong>
          </p>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-secondary" onClick={onBack} disabled={loading}>
          Back
        </button>
        <button
          className="btn btn-primary confirm-btn"
          onClick={onCreate}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="loading-spinner"></div>
              Creating Bot...
            </>
          ) : (
            <>
              🤖 Create Trading Bot
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Creating Step Component
// const CreatingStep = ({ 
//   strategy,
//   strategies,
//   token, 
//   existingPosition 
// }) => {
//   const strategyConfig = strategies[strategy];

//   return (
//     <div className="creating-step">
//       <div className="creating-animation">
//         <div className="pulse-circle"></div>
//         <div className="creating-icon">🤖</div>
//       </div>
//       <h4>Creating Trading Bot...</h4>
//       <p>
//         Setting up {strategyConfig.name} strategy for {token?.symbol}
//         {existingPosition ? ' (monitoring existing position)' : ' (with initial purchase)'}
//       </p>
//       <div className="progress-steps">
//         <div className="step completed">✓ Validating configuration</div>
//         <div className="step completed">✓ Connecting to wallet</div>
//         <div className="step active">⏳ Creating bot session</div>
//         {!existingPosition && (
//           <div className="step">⏳ Executing initial purchase</div>
//         )}
//         <div className="step">⏳ Starting price monitoring</div>
//       </div>
//     </div>
//   );
// };

// // Confirmation Step Component
// const ConfirmationStep = ({ 
//   parameters, 
//   strategy,
//   strategies,
//   selectedWallet, 
//   token, 
//   tokenInfo,
//   existingPosition,
//   priceQuote,
//   tradingFees,
//   onBack, 
//   onCreate, 
//   loading 
// }) => {
//   const strategyConfig = strategies[strategy];

//   return (
//     <div className="confirmation-step">
//       <div className="step-header">
//         <h4>Confirm Bot Configuration</h4>
//         <p>Review your settings before creating the trading bot</p>
//       </div>

//       {/* Configuration Summary */}
//       <div className="config-summary">
//         <div className="summary-section">
//           <h5>Token & Strategy</h5>
//           <div className="summary-row">
//             <span className="label">Token:</span>
//             <div className="token-display">
//               <img 
//                 src={tokenInfo?.imageUrl} 
//                 alt={tokenInfo?.name}
//                 className="token-logo-tiny"
//                 onError={(e) => {
//                   e.target.src = `https://via.placeholder.com/20x20/6366f1/ffffff?text=${tokenInfo?.symbol?.charAt(0) || '?'}`;
//                 }}
//               />
//               <span className="token-info">{tokenInfo?.symbol} - {tokenInfo?.name}</span>
//             </div>
//           </div>
//           <div className="summary-row">
//             <span className="label">Strategy:</span>
//             <div className="strategy-display">
//               <span className="strategy-icon">{strategyConfig.icon}</span>
//               <span className="strategy-name">{strategyConfig.name}</span>
//             </div>
//           </div>
//           <div className="summary-row">
//             <span className="label">Wallet:</span>
//             <div className="wallet-display">
//               <CopyableAddress
//                 address={selectedWallet?.wallet_address}
//                 truncate={true}
//                 truncateLength={8}
//               />
//             </div>
//           </div>
//         </div>

//         <div className="summary-section">
//           <h5>Trading Parameters</h5>
//           <div className="summary-row">
//             <span className="label">Stop-Loss:</span>
//             <span className="value">
//               {parameters.stopLossPercentage}% {parameters.trailingStopEnabled ? '(Trailing)' : '(Fixed)'}
//             </span>
//           </div>
//           <div className="summary-row">
//             <span className="label">Take-Profit:</span>
//             <span className="value">{parameters.takeProfitPercentage}%</span>
//           </div>
//           <div className="summary-row">
//             <span className="label">Re-entry:</span>
//             <span className="value">{parameters.reentryEnabled ? 'Enabled' : 'Disabled'}</span>
//           </div>
//         </div>

//         {/* Initial Purchase Summary */}
//         {!existingPosition && parameters.initialPurchase && (
//           <div className="summary-section">
//             <h5>Initial Purchase</h5>
//             <div className="summary-row">
//               <span className="label">Position Size:</span>
//               <span className="value">{parameters.positionSize} SOL</span>
//             </div>
//             {priceQuote && (
//               <div className="summary-row">
//                 <span className="label">Expected Tokens:</span>
//                 <span className="value">
//                   {(priceQuote.outputAmount / 1000000).toFixed(6)} {tokenInfo?.symbol}
//                 </span>
//               </div>
//             )}
//             {tradingFees && (
//               <div className="summary-row">
//                 <span className="label">Trading Fees:</span>
//                 <span className="value">{tradingFees.totalFee?.toFixed(6)} SOL</span>
//               </div>
//             )}
//           </div>
//         )}

//         {/* Existing Position Summary */}
//         {existingPosition && (
//           <div className="summary-section">
//             <h5>Existing Position</h5>
//             <div className="summary-row">
//               <span className="label">Current Balance:</span>
//               <span className="value">{existingPosition.balance?.toFixed(6)} {tokenInfo?.symbol}</span>
//             </div>
//             <div className="summary-row">
//               <span className="label">Current Value:</span>
//               <span className="value">${existingPosition.usdValue?.toFixed(2)}</span>
//             </div>
//             <div className="summary-row">
//               <span className="label">Monitoring Mode:</span>
//               <span className="value">Bot will monitor existing position</span>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Final Risk Warning */}
//       <div className="final-risk-warning">
//         <div className="warning-header">
//           <span className="warning-icon">⚠️</span>
//           <strong>Final Risk Warning</strong>
//         </div>
//         <div className="warning-content">
//           <ul>
//             <li>The bot will execute trades automatically based on market conditions</li>
//             <li>You may lose some or all of your investment</li>
//             <li>Market volatility can trigger rapid buy/sell actions</li>
//             <li>Bot performance is not guaranteed and past results don't predict future outcomes</li>
//             {parameters.initialPurchase && (
//               <li>Initial purchase will be executed immediately upon confirmation</li>
//             )}
//           </ul>
//           <p>
//             <strong>By proceeding, you acknowledge these risks and agree to automated trading.</strong>
//           </p>
//         </div>
//       </div>

//       <div className="step-actions">
//         <button className="btn btn-secondary" onClick={onBack} disabled={loading}>
//           Back
//         </button>
//         <button 
//           className="btn btn-primary confirm-btn"
//           onClick={onCreate}
//           disabled={loading}
//         >
//           {loading ? (
//             <>
//               <div className="loading-spinner"></div>
//               Creating Bot...
//             </>
//           ) : (
//             <>
//               🤖 Create Trading Bot
//             </>
//           )}
//         </button>
//       </div>
//     </div>
//   );
// };

// // Creating Step Component
const CreatingStep = ({
  strategy,
  strategies,
  token,
  existingPosition
}) => {
  const strategyConfig = strategies[strategy];

  return (
    <div className="creating-step">
      <div className="creating-animation">
        <div className="pulse-circle"></div>
        <div className="creating-icon">🤖</div>
      </div>
      <h4>Creating Trading Bot...</h4>
      <p>
        Setting up {strategyConfig.name} strategy for {token?.symbol}
        {existingPosition ? ' (monitoring existing position)' : ' (with initial purchase)'}
      </p>
      <div className="progress-steps">
        <div className="step completed">✓ Validating configuration</div>
        <div className="step completed">✓ Connecting to wallet</div>
        <div className="step active">⏳ Creating bot session</div>
        {!existingPosition && (
          <div className="step">⏳ Executing initial purchase</div>
        )}
        <div className="step">⏳ Starting price monitoring</div>
      </div>
    </div>
  );
};